import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import usePrevious from '../../../hooks/usePrevious';
import {
  GasMonitoringNode,
  GasMonitoringNodeListQuery,
  GasMonitoringNodeListResponse,
  SentroType,
  //GasMonitoringNodeLongTermEvent,
} from '../../../interfaces/GasMonitoringNode';
import reduxActions from '../../../redux/actions';
import reduxSelectors from '../../../redux/selectors';
import {CHART_COLORS} from '../../../utils/colors';
import AMSLongTermHistoryExportButton from '../../ams-sensor/AMSLongTermHistoryExportButton';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  activeId?: string;
  openedItems: string[];
  tabData?: Record<string, AMSLongTermHistoryReportDataTab>;
  onUpdate?: (updates: {
    activeId?: string;
    openedItems?: string[];
    tabData?: Record<string, AMSLongTermHistoryReportDataTab>;
  }) => void;
}

const DEFAULT_SHOWN_FIELDS = ['date', 'min', 'max', 'med', 'avg'];

interface AMSLongTermHistoryReportDataTab {
  id: string;
  refreshInterval?: number | null;
  selectedIds?: string[];
  selectAll?: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
    status?: 'active' | 'inactive' | 'unacknowledged' | 'all';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface AMSLongTermHistoryReportData {
  tabs: Record<string, AMSLongTermHistoryReportDataTab>;
}

export const getAMSLongTermHistoryReportData =
  (): AMSLongTermHistoryReportData => ({
    tabs: {},
  });

export const getAMSLongTermHistoryReportDataTab = (
  id: string
): AMSLongTermHistoryReportDataTab => ({
  id,
  selectAll: true,
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const AMSLongTermHistoryReport = ({
  activeId,
  openedItems,
  tabData,
  onUpdate,
}: Props) => {
  const reduxDispatch = useAppDispatch();

  const [liveParams, setLiveParams] = useState<
    Record<string, {date_start: string; date_end: string}>
  >({});

  const getLiveParamsForTab = useCallback(
    (tabId: string) => {
      if (liveParams[tabId]) {
        return liveParams[tabId];
      }
      return {
        date_start: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
        date_end: dayjs().format('YYYY-MM-DD'),
      };
    },
    [liveParams]
  );

  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, activeId]);

  const emoduleId = useMemo(() => {
    return activeId?.split('-')?.[0];
  }, [activeId]);

  const openedItem = useMemo(
    () =>
      activeId && tabData?.[activeId]
        ? tabData[activeId]
        : activeId
          ? getAMSLongTermHistoryReportDataTab(activeId)
          : null,
    [activeId, tabData]
  );

  const ams_emodules = useAppSelector(({assets}) => assets.ams_emodules);
  const emoduleChannelLabel = useAppSelector(
    ({assets}) => assets.sentro8ChannelLabel
  );
  const ams_nodes = useAppSelector(({assets}) =>
    assets.ams_nodes.filter((el) => el.ack === '1' && !el.non_sensing)
  );

  const tabNames = useMemo(
    () =>
      openedItems.map((id) => {
        const emodule_id = +id.split('-')?.[0];
        const emodule = ams_emodules.find(
          (i) => Number(i.id) === Number(emodule_id)
        );
        const node = ams_nodes.find(
          (i) =>
            Number(i.commtrac_external_id) ===
            Number(emodule?.commtrac_external_id)
        );
        const channel =
          emoduleChannelLabel && emodule?.channel
            ? emoduleChannelLabel[emodule?.channel] ?? ''
            : '';

        return `${node?.name}${node?.sentro_type === SentroType.SENTRO_8 ? ` (${channel})` : ''}`;
      }),
    [openedItems, ams_nodes, ams_emodules]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchGasMonitorNodes);
  }, [openedItems]);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<GasMonitoringNodeListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<GasMonitoringNodeListQuery | null>(() => {
    if (!openedItem?.id) {
      return null;
    }
    const liveDates = getLiveParamsForTab(openedItem.id);
    return {
      date_start: liveDates.date_start,
      date_end: liveDates.date_end,
      page: openedItem.params?.page ?? 0,
      limit: openedItem.params?.limit ?? 25,
      order: openedItem.params?.order ?? 'date',
      dir: openedItem.params?.dir ?? 'DESC',
      status: openedItem.params?.status ?? 'all',
    };
  }, [openedItem?.id, openedItem?.params, getLiveParamsForTab]);

  const fetchData = useCallback(
    async (params: GasMonitoringNodeListQuery) => {
      if (!emoduleId) {
        return;
      }
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/ams/${emoduleId}/long-term-history`;
        const resp = await API.get<GasMonitoringNodeListResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_5649f740');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [emoduleId]
  );

  useEffect(() => {
    if (params && emoduleId) {
      fetchData(params);
    }
  }, [params, emoduleId, fetchData]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/

  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);

  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*********/
  /* grid */
  /*********/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<any>[] = [
    {
      field: 'select',
      type: 'select',
      width: 60,
      hideable: false,
      doNotExport: true,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(`${row.id}`)}
          onChange={() => toggleSelectItem(`${row.id}`)}
        />
      ),
    },
    {
      field: 'date',
      headerName: 'Timestamp',
      width: 160,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.date,
    },
    {
      field: 'min',
      headerName: 'Minimum',
      width: 100,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.min,
    },
    {
      field: 'max',
      headerName: 'Maximum',
      width: 100,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.max,
    },
    {
      field: 'med',
      headerName: 'Median',
      width: 100,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.med,
    },
    {
      field: 'avg',
      headerName: 'Average',
      width: 100,
      resizeable: true,
      sortable: true,
      valueGetter: ({row}) => row.avg,
    },
  ];

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = useMemo(
    () =>
      openedItem?.selectAll
        ? rows.map((it) => it.id.toString())
        : openedItem?.selectedIds ?? [],
    [rows, openedItem]
  );

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems?.includes(`${i.id}`)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const toggleSelectItem = (id: string) => {
    if (openedItem && activeId) {
      if (selectedItems?.includes(id)) {
        onUpdate?.({
          tabData: {
            ...tabData,
            [activeId]: {
              ...openedItem,
              selectedIds: selectedItems.filter((i) => i !== id),
              selectAll: false,
            },
          },
        });
      } else {
        onUpdate?.({
          tabData: {
            ...tabData,
            [activeId]: {
              ...openedItem,
              selectedIds: [...(selectedItems ?? []), id],
              selectAll: selectedItems.length + 1 === fetchedData?.items.length,
            },
          },
        });
      }
    }
  };

  const selectAll = () => {
    if (openedItem && activeId) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            selectAll: true,
            selectedIds: rows?.map((i) => `${i.id}`) ?? [],
          },
        },
      });
    }
  };

  const unselectAll = () => {
    if (openedItem && activeId) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            selectAll: false,
            selectedIds: [],
          },
        },
      });
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const dateTimeFormatter = (timestamp: string) => {
    return dayjs(timestamp).format('ddd, MMM D, YYYY');
  };

  const isDarkMode = useAppSelector(reduxSelectors.app.getIsDarkMode);

  const lines = [
    {
      key: 'min',
      label: 'Min',
    },
    {
      key: 'max',
      label: 'Max',
    },
    {
      key: 'med',
      label: 'Med',
    },
    {
      key: 'avg',
      label: 'Avg',
    },
  ];

  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);

  const toggleKey = (key: string) => {
    setHiddenKeys(
      hiddenKeys.includes(key)
        ? hiddenKeys.filter((i) => i !== key)
        : [...hiddenKeys, key]
    );
  };

  const chartData = useMemo(
    () => orderBy(selectedRows, (i) => i.date),
    [selectedRows]
  );

  const prevSelectedAll = usePrevious(selectedAll);

  useEffect(() => {
    if ((prevSelectedAll && !selectedAll) || !openedItem?.selectedIds) {
      selectAll();
    }
  }, [rows]);

  const exportFields = columns
    .filter((col) => !col.doNotExport)
    .map((col) => ({
      field: col.field,
      label: col.headerName ?? col.field,
      hidden: shownFields?.indexOf(col.field) === -1,
    }));

  const openTab = (id: string) => {
    if (!openedItems.includes(id)) {
      onUpdate?.({
        openedItems: [...openedItems, id],
        activeId: id,
        tabData: {
          ...tabData,
          [id]: getAMSLongTermHistoryReportDataTab(id),
        },
      });
    } else {
      onUpdate?.({
        activeId: id,
      });
    }
  };

  const closeTab = (id: string) => {
    const newOpenedItems = openedItems.filter((i) => i !== id);
    const newTabData = {...tabData};
    delete newTabData[id];
    onUpdate?.({
      openedItems: newOpenedItems,
      activeId:
        newOpenedItems.length > 0
          ? newOpenedItems[newOpenedItems.length - 1]
          : undefined,
      tabData: newTabData,
    });
  };

  const changeTab = (_: any, id: string) => {
    onUpdate?.({
      activeId: id,
    });
  };

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem && activeId) {
      onUpdate?.({
        tabData: {
          ...tabData,
          [activeId]: {
            ...openedItem,
            columnWidths: widths,
          },
        },
      });
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem && activeId && tabData) {
        onUpdate?.({
          tabData: {
            ...tabData,
            [activeId]: {
              ...openedItem,
              shownFields: fields,
            },
          },
        });
      }
    },
    [onUpdate, activeId, tabData, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem && activeId && tabData) {
        onUpdate?.({
          tabData: {
            ...tabData,
            [activeId]: {
              ...openedItem,
              columnOrder: order,
              shownFields: fields,
            },
          },
        });
      }
    },
    [onUpdate, activeId, tabData, openedItem]
  );

  const getListName = (sensor: GasMonitoringNode) => {
    const channel =
      emoduleChannelLabel && sensor.channel
        ? emoduleChannelLabel[sensor.channel] ?? ''
        : '';

    return `${sensor?.name}${sensor?.sentro_type === SentroType.SENTRO_8 ? ` (${channel})` : ''}`;
  };

  return (
    <>
      <DashboardPanelTitleSlot>
        AMS Long Term History Report
      </DashboardPanelTitleSlot>

      <ResizableColumns
        left={
          <Box
            width="100%"
            height="100%"
            minWidth={400}
            pt={2}
            pb={2}
            pr={6}
            bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{left: 20, top: 40}}>
                <XAxis dataKey="date" />
                <YAxis>
                  <Label value="Latency (ms)" dx={-30} angle={-90} />
                </YAxis>
                <RechartsTooltip
                  labelStyle={{color: 'black'}}
                  labelFormatter={dateTimeFormatter}
                />
                <CartesianGrid opacity={0.3} />
                <Legend onClick={(item) => toggleKey(item.dataKey)} />
                {lines?.map(({key, label}, idx) => (
                  <Line
                    key={key}
                    dataKey={key}
                    name={label}
                    hide={hiddenKeys.includes(key)}
                    stroke={CHART_COLORS[idx]}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        }
      >
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            py={1.5}
            bgcolor={(theme) =>
              theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
            }
          >
            {isOpenAwayFromConnectView && (
              <Box pr={1.5}>
                <PopupState variant="popover" popupId="demo-popup-menu">
                  {(popupState) => (
                    <>
                      <Button variant="outlined" {...bindTrigger(popupState)}>
                        Select an Object
                      </Button>
                      <Menu {...bindMenu(popupState)}>
                        {ams_nodes?.map((it) => (
                          <MenuItem
                            key={`${it.emodule_id}-${it.id}`}
                            onClick={() => {
                              it?.emodule_id &&
                                it?.id &&
                                openTab(`${it.emodule_id}-${it.id}`);
                              popupState.close();
                            }}
                          >
                            {getListName(it)}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </PopupState>
              </Box>
            )}
            {openedItems.length ? (
              <Box pr={2}>
                <Tabs
                  value={openedItem?.id}
                  variant="scrollable"
                  onChange={changeTab}
                >
                  {openedItems.map((id, idx) => (
                    <Tab
                      key={id}
                      value={id}
                      label={
                        <TabLabel
                          name={tabNames?.[idx] ?? 'Undefined'}
                          onClose={() => closeTab(id)}
                        />
                      }
                    />
                  ))}
                </Tabs>
              </Box>
            ) : (
              <Box minWidth={400} pr={1.5}>
                <Alert severity="warning">No Assets Opened</Alert>
              </Box>
            )}

            <Box display="flex" flexDirection="column" pr={1.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <Box display="flex" gap={1}>
                  <Box minWidth={400}>
                    <DateRangeSelect
                      maxDays={365}
                      value={
                        openedItem && activeId
                          ? [
                              dayjs(
                                getLiveParamsForTab(activeId).date_start
                              ).toDate(),
                              dayjs(
                                getLiveParamsForTab(activeId).date_end
                              ).toDate(),
                            ]
                          : undefined
                      }
                      size="small"
                      disabled={!openedItem}
                      onChange={(v) => {
                        if (openedItem && activeId) {
                          setLiveParams((prev) => ({
                            ...prev,
                            [activeId]: {
                              date_start: v?.[0]
                                ? dayjs(v[0]).format('YYYY-MM-DD')
                                : dayjs()
                                    .subtract(90, 'day')
                                    .format('YYYY-MM-DD'),
                              date_end: v?.[1]
                                ? dayjs(v[1]).format('YYYY-MM-DD')
                                : dayjs().format('YYYY-MM-DD'),
                            },
                          }));
                          onUpdate?.({
                            tabData: {
                              ...tabData,
                              [activeId]: {
                                ...openedItem,
                                selectAll: true,
                                params: {
                                  ...openedItem.params,
                                  page: 0,
                                },
                              },
                            },
                          });
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Box display="flex" sx={{flex: 1, minWidth: 0}}>
                  <Box sx={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                    <AdaptiveButtonGroup disabled={!openedItem}>
                      <Button size="small" data-priority={1}>
                        <AMSLongTermHistoryExportButton
                          value={{
                            exportFields: exportFields,
                            params: params ?? undefined,
                            emoduleId: emoduleId,
                          }}
                        />
                      </Button>

                      <Button
                        size="small"
                        onClick={() => params && fetchData(params)}
                        data-priority={2}
                      >
                        <RefreshIcon />
                      </Button>

                      <AutoRefreshSelect
                        value={openedItem?.refreshInterval ?? null}
                        data-priority={3}
                        onChange={(v) => {
                          if (openedItem && activeId) {
                            onUpdate?.({
                              tabData: {
                                ...tabData,
                                [activeId]: {
                                  ...openedItem,
                                  refreshInterval: v,
                                },
                              },
                            });
                          }
                        }}
                      />

                      <Button
                        size="small"
                        onClick={() => dataGridRef.current?.printTable()}
                        data-priority={4}
                      >
                        <PrintIcon />
                      </Button>
                    </AdaptiveButtonGroup>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Backdrop
            open={fetchedInProgress}
            sx={{position: 'absolute', zIndex: 1199}}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {fetchedErrors.map((error, idx) => (
            <Alert
              key={idx}
              severity="error"
              onClose={() => params && fetchData(params)}
            >
              {error}
            </Alert>
          ))}

          <DataGrid
            ref={dataGridRef}
            rows={rows}
            columns={columns}
            loading={fetchedInProgress}
            shownFields={shownFields}
            defaultShownFields={DEFAULT_SHOWN_FIELDS}
            columnWidths={openedItem?.columnWidths}
            columnOrder={openedItem?.columnOrder}
            pagination
            paginationMode="server"
            size="small"
            sortBy={
              params?.order
                ? {
                    field: params?.order,
                    dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                  }
                : null
            }
            sortingMode="server"
            page={params?.page}
            pageSize={params?.limit}
            rowCount={fetchedData?.count}
            sxFooter={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
            }}
            onPageChange={(v) => {
              if (openedItem && activeId) {
                onUpdate?.({
                  tabData: {
                    ...tabData,
                    [activeId]: {
                      ...openedItem,
                      selectAll: true,
                      params: {
                        ...openedItem.params,
                        page: v,
                      },
                    },
                  },
                });
              }
            }}
            onPageSizeChange={(v) => {
              if (openedItem && activeId) {
                onUpdate?.({
                  tabData: {
                    ...tabData,
                    [activeId]: {
                      ...openedItem,
                      selectAll: true,
                      params: {
                        ...openedItem.params,
                        page: 0,
                        limit: v,
                      },
                    },
                  },
                });
              }
            }}
            onSort={(v) => {
              if (v && openedItem && activeId) {
                onUpdate?.({
                  tabData: {
                    ...tabData,
                    [activeId]: {
                      ...openedItem,
                      params: {
                        ...openedItem.params,
                        order: v.field,
                        dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                      },
                    },
                  },
                });
              }
            }}
            onShownFieldsChange={handleShownFieldsChange}
            onColumnWidthsChange={handleColumnWidthChange}
            onColumnOrderAndShownFieldsChange={
              handleColumnOrderAndShownFieldsChange
            }
          />
        </Box>
      </ResizableColumns>
    </>
  );
};
